import {
    Button,
    Checkbox,
    Divider,
    Input,
    LabeledContainer,
    Notification,
    Radio,
    Select,
    Spinner,
    Toggle,
} from '@schibsted-svp/react-ui';
import AppliedFilters from 'components/AssetsList/AppliedFilters';
import { FiltersDialog } from 'components/core/FiltersDialog';
import { useCategoryCustomFieldsPerType } from 'components/core/FiltersDialog/hooks';
import OrderDropdown from 'components/core/OrderDropdown';
import { findMultipleOptions, getValuesFromOptionsArray } from 'components/ui/Form/Select';
import ScreenContainer from 'components/ui/ScreenContainer';
import Tooltip from 'components/ui/Tooltip';
import config from 'config';
import TravoltaGif from 'gifs/so-really.gif';
import { useUserPermissions } from 'hooks/usersManagement/useUserPermissions';
import { getFormattedTime } from 'lib/time';
import { debounce, omit } from 'lodash';
import React, { ChangeEvent, useEffect, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { Controller, useFieldArray } from 'react-hook-form';
import { MdAccessTime, MdHelp, MdVideoLibrary } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { LABELS } from 'screens/NewsroomScreen/PlaylistsScreen/utils';
import { playlistsFiltersClear } from 'store/playlists/filters';
import { playlistsGeneratorAssetsFetchSuccess } from 'store/playlists/items';
import { getPlaylistFetchingState, isPlaylistExisting } from 'store/playlists/selectors';

import { FiltersDialogData } from 'components/core/FiltersDialog/types';
import isBoolean from 'lodash/isBoolean';
import { createFilterString } from 'models/filter';
import { svpApiProxyClient } from 'services';
import { prepareQueryString } from 'services/api-client/assets';
import { removeEmptyValues } from 'store/asset-list/reducers';
import { useAppSelector } from 'store/hooks';
import generateSearchParameters from 'store/utils';
import { CustomizeThemeDialog } from './CustomizeThemeDialog';
import { usePlaylistDuration } from './hooks';
import { PlaylistMetadataDialog } from './PlaylistMetadataDialog';
import { PlaylistsFormItems } from './PlaylistsFormItems';
import { usePlaylistForm } from './usePlaylistForm';

import type { FormValues, Playlist, PlaylistItem, PlaylistItemAsset } from './types';

import css from './PlaylistsScreenGenerator.module.scss';

const {
    sortTypes,
    playlists,
    publicDisplay: { dashboardUrl: publicDisplayBaseUrl },
} = config;
const defaultSortTypeValue = sortTypes[2].value;

const playlistLabelOptions = playlists.labels.map(({ value, label }) => ({
    value: value as string,
    label: label as string,
}));

type Sort =
    | {
          readonly value: 'created' | 'published' | 'relevance' | 'flightTimes.start';
          readonly label: string;
      }
    | { value: string };

type Filter = { filter: keyof FiltersDialogData; value: FiltersDialogData[keyof FiltersDialogData] };
interface PlaylistsScreenGeneratorProps {
    provider: string;
    playlist?: Playlist;
    order?: string;
    mode: 'new' | 'edit';
    pristine?: boolean;
}

export const getTypeDependentFilters = (filters: FiltersDialogData, type: string) => {
    switch (type) {
        case LABELS.PODCAST:
            return { ...filters, isPodcast: true, assetTypes: [{ label: 'audio', value: 'audio' }] };
        case LABELS.AUDIO:
            return { ...filters, assetTypes: [{ label: 'audio', value: 'audio' }] };

        default:
            return { ...filters, assetTypes: [{ label: 'video', value: 'video' }] };
    }
};

const getAppliedFilters = (
    provider: string,
    searchQuery: string,
    query: string,
    filters: FiltersDialogData,
    items: PlaylistItem[]
) => {
    const { newsrooms } = filters;

    const customFilters: FiltersDialogData = omit(filters, ['statuses', 'assetTypes', 'types', 'newsrooms']);
    const areFiltersEmpty = !query && Object.values(customFilters).filter(Boolean).length === 0;
    const onlyCurrentNewsroom = newsrooms?.length === 1 && newsrooms.includes(provider);

    if ((areFiltersEmpty && onlyCurrentNewsroom) || !searchQuery) {
        return {};
    }

    const excludedAssets = customFilters.excludedAssets?.filter(
        (excludedAssetId) =>
            !items.some((item) => item.isStatic && 'asset' in item && item.asset.id === excludedAssetId)
    );

    const appliedFilters = {
        ...omit(customFilters, 'customFields', 'excludedAssets'),
        ...customFilters.customFields,
        ...(excludedAssets?.length && { excludedAssets }),
    };

    if (onlyCurrentNewsroom) {
        return appliedFilters;
    }

    return {
        ...appliedFilters,
        newsrooms: newsrooms?.map((value) => ({
            value,
            label: config.newsrooms[value],
        })),
    };
};

function getCleanedFilters(currentFilters, { filter, value, newsroom }) {
    const newFilters = { ...currentFilters };

    if (newFilters?.customFields && Object.keys(newFilters.customFields).includes(filter)) {
        return {
            ...newFilters,
            customFields: omit(newFilters.customFields, filter),
        };
    }

    if (isBoolean(value)) {
        return {
            ...newFilters,
            [filter]: false,
        };
    }

    if (filter === 'categories' && newFilters?.customFields) {
        delete newFilters.customFields;
    }

    const newFilter = ['after', 'before', 'min', 'max', 'categories', 'createdBy'].includes(filter)
        ? null
        : newFilters[filter].filter((item) => (typeof item === 'object' ? item.value !== value : item !== value));

    if (!newFilter || newFilter.length === 0) {
        if (filter === 'newsrooms') {
            return {
                ...newFilters,
                newsrooms: [newsroom],
            };
        }
        return omit(newFilters, [filter]);
    }

    newFilters[filter] = newFilter;

    return newFilters;
}

// eslint-disable-next-line consistent-return
async function fetchFilteredAssets({
    filters,
    sortType,
    podcastOnly,
    setIsSearching,
    searchQuery,
    setValue,
    limit,
    query,
    provider,
}: {
    filters: FiltersDialogData;
    sortType?: Sort[];
    podcastOnly?: boolean;
    setIsSearching: (isSearching: boolean) => void;
    searchQuery: string;
    setValue: UseFormSetValue<FormValues>;
    limit: number;
    query: string;
    provider: string;
}) {
    try {
        const audioFilters = {
            assetTypes: [{ label: 'audio', value: 'audio' }],
        };
        const podcastFilters = {
            isPodcast: true,
            ...audioFilters,
        };
        setIsSearching(true);
        const searchParameters = generateSearchParameters(
            { ...filters, ...(podcastOnly ? podcastFilters : {}) },
            sortType[0] || {
                value: new URLSearchParams(searchQuery).get('sort') || 'created',
            }
        );
        const newsroomFilters = filters?.newsrooms;
        const areAssetsFromCurrentNewsroom = newsroomFilters?.length === 1 && newsroomFilters.includes(provider);
        const preparedQuery = prepareQueryString({
            query,
            ...(!areAssetsFromCurrentNewsroom && { searchQueryProviders: newsroomFilters }),
            before: searchParameters.before,
            after: searchParameters.after,
            filter: createFilterString(searchParameters.filters),
            order: searchParameters.order,
            limit,
        });
        setValue('searchQuery', new URLSearchParams(preparedQuery).toString());
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { assets } = await svpApiProxyClient.fetchFilteredAssets({
            provider,
            searchQueryProviders: areAssetsFromCurrentNewsroom ? provider : newsroomFilters,
            query,
            limit,
            ...searchParameters,
        });
        return assets;
    } catch {
        Notification.notify.error('There was an error during filtering videos');
    }
}

export function PlaylistsScreenGenerator({
    provider,
    playlist,
    order,
    mode,
    pristine = true,
}: PlaylistsScreenGeneratorProps) {
    const { formApi, onSubmit } = usePlaylistForm({ playlist });
    const { register, control, getValues, setValue, formState, watch, trigger } = formApi;

    const fieldArrayApi = useFieldArray({
        control,
        name: 'items',
    });

    const [sort, setSort] = useState(order ? { value: order } : sortTypes[0]);
    const [isSearching, setIsSearching] = useState(false);
    const dispatch = useDispatch();
    const { newsroom, playlistId } = useParams();
    const { errors } = formState;

    const watchedValues = watch([
        'searchQuery',
        'isPodcastPlaylist',
        'type',
        'limit',
        'items',
        'query',
        'partnerName',
        'partnerPlaylistAlias',
    ]);

    const [searchQuery, isPodcastPlaylist, type, limit, items, query, partnerName, partnerPlaylistAlias] =
        watchedValues;

    const staticItems = items.filter(({ isStatic }) => isStatic);

    const userPermissions = useUserPermissions();

    const queryFromSearch = new URLSearchParams(searchQuery).get('query');

    const isRegularPlaylist = type !== LABELS.POP;
    const duration = usePlaylistDuration({ items, isRegularPlaylist });

    const initialFilters = useAppSelector((state) => state.playlists.filters);
    const [filters, setFilters] = useState(initialFilters);
    const playlistExists = useSelector((state) => isPlaylistExisting(state, provider, playlistId));
    const [typeDependentFilters, setTypeDependentFilters] = useState(getTypeDependentFilters(filters, type));

    useEffect(() => {
        setFilters(initialFilters);
    }, [initialFilters]);

    useEffect(() => {
        setTypeDependentFilters(getTypeDependentFilters(filters, type));
    }, [filters, isPodcastPlaylist, type]);

    useEffect(() => {
        if (order) {
            setSort({ value: order });
        } else {
            setSort({ value: defaultSortTypeValue });
        }
    }, [order]);

    const isPlaylistFetching = useSelector((state) => getPlaylistFetchingState(state, { newsroom, playlistId }));

    const handleSubmit = onSubmit(mode, provider, isRegularPlaylist);

    const handleSearch = async ({
        sortType = [sort],
        podcastOnly = isPodcastPlaylist,
        currentSearchQuery,
        currentQuery,
        currentFilters,
        currentLimit,
        currentItems = items,
    }: {
        sortType?: Sort[];
        podcastOnly?: boolean;
        currentSearchQuery: string;
        currentQuery: string;
        currentFilters: FiltersDialogData;
        currentLimit: number;
        currentItems?: PlaylistItem[];
    }) => {
        const showHiddenDefault = !(currentFilters?.showHidden === undefined);

        const pinnedAssetIds = currentItems
            .filter((item) => item.isStatic && 'asset' in item)
            .map((item: PlaylistItemAsset) => item.asset.id);

        const adjustedFilters = {
            ...currentFilters,
            ...(mode === 'new' && { showHidden: showHiddenDefault }),
            ...(pinnedAssetIds.length && {
                excludedAssets: [...new Set([...(currentFilters?.excludedAssets || []), ...pinnedAssetIds])],
            }),
        };

        const assets = await fetchFilteredAssets({
            filters: adjustedFilters,
            sortType,
            podcastOnly,
            setIsSearching,
            searchQuery: currentSearchQuery,
            setValue,
            limit: currentLimit,
            query: currentQuery,
            provider,
        });

        dispatch(
            playlistsGeneratorAssetsFetchSuccess({
                assets,
                provider,
                playlistId,
            })
        );
        const allItems = assets.map((asset) => ({
            asset: {
                id: asset.id,
                provider: asset.provider,
                category: asset.category.title,
            },
        }));
        const staticVideos = currentItems
            .map((item, index) => ({ ...item, position: index }))
            .filter(({ isStatic }) => isStatic);
        staticVideos.forEach((item) => {
            allItems.splice(item.position, 0, item);
        });
        fieldArrayApi.replace(allItems.map((item, index) => ({ ...item, position: index })));
        setIsSearching(false);
    };

    const handleSortTypeChange = (sortType: Sort) => {
        setSort(sortType);
        handleSearch({
            sortType: [sortType],
            currentSearchQuery: searchQuery,
            currentQuery: query,
            currentFilters: filters,
            currentLimit: limit,
        });
    };

    const handleQueryDeletion = async () => {
        setValue('query', '');
        const searchParams = new URLSearchParams(searchQuery);
        searchParams.delete('query');

        setValue('searchQuery', searchParams.toString());

        await handleSearch({
            currentSearchQuery: searchParams.toString(),
            currentQuery: '',
            currentFilters: filters,
            currentLimit: limit,
        });
    };

    const handleFiltersClear = () => {
        const values = getValues();

        const currentStaticItems = values?.items?.filter(({ isStatic }) => isStatic) || [];
        const excludedAssets = filters.excludedAssets?.filter((excludedAssetId) =>
            currentStaticItems?.some((item) => 'asset' in item && item.asset.id === excludedAssetId)
        );

        dispatch(playlistsFiltersClear(newsroom, { ...(excludedAssets?.length && { excludedAssets }) }));
        setTypeDependentFilters(getTypeDependentFilters(filters, type));

        setValue('searchQuery', '');
        setValue('query', '');
        setValue('items', currentStaticItems);
    };

    const handleFiltersDeletion = (filtersToRemove: Filter) => {
        let podcastOnly = isPodcastPlaylist;
        if (filtersToRemove.filter === 'isPodcast') {
            setValue('isPodcastPlaylist', false);

            podcastOnly = false;
        }

        const nextFilters = getCleanedFilters(filters, {
            filter: filtersToRemove.filter,
            value: filtersToRemove.value,
            newsroom,
        });

        setFilters(nextFilters);
        handleSearch({
            podcastOnly,
            currentSearchQuery: searchQuery,
            currentQuery: query,
            currentFilters: nextFilters,
            currentLimit: limit,
        });
    };

    const handleFilter = async (dialogFilters: FiltersDialogData) => {
        setValue('isPodcastPlaylist', dialogFilters.isPodcast);

        const adjustedFilters = {
            ...removeEmptyValues(dialogFilters),
            showHidden: dialogFilters?.showHidden,
            excludeArchivedCategories: dialogFilters?.excludeArchivedCategories,
        };

        setFilters(adjustedFilters);
        await handleSearch({
            currentSearchQuery: searchQuery,
            currentQuery: query,
            currentFilters: adjustedFilters,
            currentLimit: limit,
        });
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && isRegularPlaylist && !errors.limit) {
            event.preventDefault();
        }
    };

    const onPodcastFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFilters(getCleanedFilters(filters, { filter: 'isPodcast', value: true, newsroom }));

        handleSearch({
            currentSearchQuery: searchQuery,
            currentQuery: query,
            podcastOnly: event.target.checked,
            currentFilters: filters,
            currentLimit: limit,
        });
    };

    const onQueryChange = debounce((e) => {
        setValue('query', e.target.value);

        handleSearch({
            currentSearchQuery: searchQuery,
            currentQuery: e.target.value,
            currentFilters: filters,
            currentLimit: limit,
        });
    }, 500);

    const onLimitChange = (e) => {
        setValue('limit', e.target.valueAsNumber);

        handleSearch({
            currentSearchQuery: searchQuery,
            currentQuery: query,
            currentFilters: filters,
            currentLimit: e.target.valueAsNumber,
        });
    };

    const pinAssetItem = async (index: number) => {
        const item = items[index];

        if (!item || !('asset' in item)) {
            return;
        }

        const newItems = [...items];
        newItems[index] = { ...item, isStatic: true };

        const newFilters = {
            ...filters,
            excludedAssets: [...(filters.excludedAssets || []), item.asset.id],
        };

        await handleSearch({
            currentSearchQuery: searchQuery,
            currentQuery: query,
            currentFilters: newFilters,
            currentLimit: limit,
            currentItems: newItems,
        });

        setFilters(newFilters);
    };

    const removeAssetItem = async (index: number) => {
        const item = items[index];

        if (!item || !('asset' in item)) {
            return;
        }

        const excludedAssets = filters.excludedAssets || [];
        const isExcludedAsset = excludedAssets.includes(item.asset.id);

        if (!isExcludedAsset && item.isStatic) {
            fieldArrayApi.remove(index);
            return;
        }

        const newItems = [...items];

        if (item.isStatic) {
            newItems[index] = { ...item, isStatic: false };
        }

        const newFilters = {
            ...filters,
            excludedAssets: isExcludedAsset
                ? excludedAssets.filter((id: number) => id !== item.asset.id)
                : [...excludedAssets, item.asset.id],
        };

        await handleSearch({
            currentSearchQuery: searchQuery,
            currentQuery: query,
            currentFilters: newFilters,
            currentLimit: limit,
            currentItems: newItems,
        });

        setFilters(newFilters);
    };

    const categoryCustomDateFields = useCategoryCustomFieldsPerType({
        provider: newsroom,
        type: 'date',
        filters,
    });
    const categoryCustomBooleanFields = useCategoryCustomFieldsPerType({
        provider: newsroom,
        type: 'checkbox',
        filters,
    });

    if (mode === 'edit' && !playlistExists) {
        if (isPlaylistFetching) {
            return (
                <ScreenContainer>
                    <Spinner containerClassName={css.spinner} />
                </ScreenContainer>
            );
        }

        return (
            <div className={css.notFound}>
                Requested playlist can&apos;t be found
                <img alt="404" src={TravoltaGif} className={css.travolta} />
            </div>
        );
    }

    return (
        <ScreenContainer>
            <form onSubmit={handleSubmit} className={css.form}>
                <div onKeyDown={handleOnKeyDown} className={css.meta}>
                    <LabeledContainer label="Playlist title">
                        <Input
                            {...register('name')}
                            size="small"
                            className={css.formInputs}
                            placeholder="name"
                            autoComplete="off"
                            error={errors?.name?.message}
                        />
                    </LabeledContainer>
                    <LabeledContainer
                        label={
                            <span>
                                Header{' '}
                                <Tooltip
                                    theme="light"
                                    content="Playlist name displayed on the frontend, mobile apps, etc."
                                    placement="top"
                                    offset={[0, 10]}
                                >
                                    <span className={css.helpIcon}>
                                        <MdHelp />
                                    </span>
                                </Tooltip>
                            </span>
                        }
                    >
                        <Input {...register('header')} size="small" className={css.formInputs} placeholder="header" />
                    </LabeledContainer>
                    {config.publicDisplay.newsroomsWithPlaylistUIExtension.includes(provider) && (
                        <>
                            <div className={css.partnerRow}>
                                <LabeledContainer
                                    label={
                                        <span>
                                            Partner name{' '}
                                            <Tooltip
                                                theme="light"
                                                content="Used in public displays to identify the playlist. Both partner name and partner playlist alias are needed to link the playlist with public displays."
                                                placement="top"
                                                offset={[0, 10]}
                                            >
                                                <span className={css.helpIcon}>
                                                    <MdHelp />
                                                </span>
                                            </Tooltip>
                                        </span>
                                    }
                                >
                                    <Input
                                        {...register('partnerName', {
                                            onChange: () => trigger('partnerPlaylistAlias'),
                                        })}
                                        size="small"
                                        className={css.formInputs}
                                        placeholder="partner name"
                                        error={errors?.partnerName?.message}
                                    />
                                </LabeledContainer>
                                <LabeledContainer
                                    label={
                                        <span>
                                            Partner playlist alias{' '}
                                            <Tooltip
                                                theme="light"
                                                content="Used in public displays to identify the playlist. Both partner name and partner playlist alias are needed to link the playlist with public displays."
                                                placement="top"
                                                offset={[0, 10]}
                                            >
                                                <span className={css.helpIcon}>
                                                    <MdHelp />
                                                </span>
                                            </Tooltip>
                                        </span>
                                    }
                                >
                                    <Input
                                        {...register('partnerPlaylistAlias', {
                                            onChange: () => trigger('partnerName'),
                                        })}
                                        size="small"
                                        className={css.formInputs}
                                        placeholder="partner playlist alias"
                                        error={errors?.partnerPlaylistAlias?.message}
                                    />
                                </LabeledContainer>
                            </div>
                            <LabeledContainer
                                label={
                                    <span>
                                        Show only one latest asset per category in Public Display{' '}
                                        <Tooltip
                                            theme="light"
                                            content="When enabled, only the most recent asset from each category appears in the display queue."
                                            placement="top"
                                            offset={[0, 10]}
                                        >
                                            <span className={css.helpIcon}>
                                                <MdHelp />
                                            </span>
                                        </Tooltip>
                                    </span>
                                }
                            >
                                <Controller
                                    name="useLimitPerCategory"
                                    control={control}
                                    render={({ field }) => (
                                        <Toggle
                                            checked={field.value}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                field.onChange(e.target.checked)
                                            }
                                        />
                                    )}
                                />
                            </LabeledContainer>
                            <LabeledContainer
                                label={
                                    <span>
                                        Public Display queue{' '}
                                        <Tooltip
                                            theme="light"
                                            content="Shows actual videos currently in the public display queue. Note: assets may be limited or discarded based on category filtering or asset lifetime settings."
                                            placement="top"
                                            offset={[0, 10]}
                                        >
                                            <span className={css.helpIcon}>
                                                <MdHelp />
                                            </span>
                                        </Tooltip>
                                    </span>
                                }
                            >
                                <a
                                    href={`${publicDisplayBaseUrl}${partnerName ? `/${partnerName}` : ''}${partnerPlaylistAlias ? `#${partnerPlaylistAlias}` : ''}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={css.publicDisplayLink}
                                >
                                    <Button size="small" variant="standard" type="button">
                                        View Public Display videos
                                    </Button>
                                </a>
                            </LabeledContainer>
                        </>
                    )}
                    <div className={css.metaRow}>
                        <LabeledContainer label="Labels">
                            <Controller
                                name="labels"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isMulti
                                        options={playlistLabelOptions}
                                        value={findMultipleOptions(playlistLabelOptions)(field.value)}
                                        onChange={(value) => field.onChange(getValuesFromOptionsArray(value))}
                                        width={340}
                                        size="small"
                                    />
                                )}
                            />
                        </LabeledContainer>
                        <LabeledContainer label="Playlist type">
                            <div className={css.type}>
                                <Radio {...register('type')} defaultValue={LABELS.VIDEO} label="Video" disabled />
                                <Radio {...register('type')} defaultValue={LABELS.AUDIO} label="Audio" disabled />
                                <Radio {...register('type')} defaultValue={LABELS.POP} label="Playlists" disabled />
                                {type === LABELS.AUDIO ? (
                                    <Checkbox
                                        {...register('isPodcastPlaylist')}
                                        defaultValue={LABELS.PODCAST}
                                        onChange={onPodcastFilterChange}
                                        label="Podcasts only"
                                    />
                                ) : null}
                            </div>
                        </LabeledContainer>
                        <LabeledContainer label="Playlist duration" className={css.durationContainer}>
                            <div className={css.duration}>
                                <MdAccessTime color={css.silver2} size={15} className={css.icon} />
                                {getFormattedTime(duration)}
                            </div>
                        </LabeledContainer>
                    </div>
                    <div className={css.metaActions}>
                        <PlaylistMetadataDialog formApi={formApi} handleSubmit={handleSubmit} />
                        {window.__ADMIN_ENV__ !== 'production' && isRegularPlaylist && (
                            <CustomizeThemeDialog playlist={playlist} />
                        )}
                        {/* there is no backend yet so we dont render this component for production */}
                    </div>
                    <Divider margin="5px 0" />
                    {isRegularPlaylist && (
                        <div className={css.criteria}>
                            <LabeledContainer label="Generate playlist based on">
                                <Input
                                    {...register('query')}
                                    size="small"
                                    className={css.formInputs}
                                    placeholder="Search"
                                    autoComplete="off"
                                    onChange={onQueryChange}
                                />
                            </LabeledContainer>
                            <LabeledContainer>
                                <OrderDropdown
                                    size="small"
                                    selectedSort={sort}
                                    onChange={handleSortTypeChange}
                                    className={css.order}
                                    menuGap={14}
                                    menuArrowPosition={125}
                                />
                            </LabeledContainer>
                            <LabeledContainer>
                                <FiltersDialog
                                    provider={provider}
                                    onFilter={handleFilter}
                                    stopFormPropagation
                                    initialValues={typeDependentFilters}
                                    newsroomsSelect
                                    showHiddenCheckbox
                                    excludeArchivedCategoriesCheckbox
                                    hideAssetTypeField
                                    dialogTrigger={(onClick) => (
                                        <Button onClick={onClick} type="button" size="small" variant="standard">
                                            Filters
                                        </Button>
                                    )}
                                />
                            </LabeledContainer>
                            <LabeledContainer label="Search query limit">
                                <Input
                                    {...register('limit', { valueAsNumber: true })}
                                    type="number"
                                    min={1}
                                    max={100}
                                    icon={<MdVideoLibrary color={css.purpleHeart} />}
                                    iconPosition="right"
                                    size="small"
                                    className={css.formInputs}
                                    placeholder="limit"
                                    autoComplete="off"
                                    error={errors.limit?.message}
                                    onChange={onLimitChange}
                                />
                            </LabeledContainer>
                        </div>
                    )}
                    {isRegularPlaylist && (
                        <AppliedFilters
                            onFilterDeletion={handleFiltersDeletion}
                            onFiltersClear={handleFiltersClear}
                            query={queryFromSearch}
                            filters={getAppliedFilters(provider, searchQuery, query, typeDependentFilters, items)}
                            onQueryDeletion={handleQueryDeletion}
                            categoryCustomDateFields={categoryCustomDateFields}
                            categoryCustomBooleanFields={categoryCustomBooleanFields}
                            clearButtonLabel="Remove dynamic elements"
                            forceShow={items.length !== staticItems.length}
                        />
                    )}
                </div>
                <PlaylistsFormItems
                    fieldArrayApi={fieldArrayApi}
                    isRegularPlaylist={isRegularPlaylist}
                    getValues={getValues}
                    provider={provider}
                    formState={formState}
                    isPristine={pristine}
                    isPlaylistFetching={isPlaylistFetching}
                    disabledSaving={
                        !userPermissions?.isUser ||
                        Boolean(errors.limit) ||
                        Boolean(errors.partnerName) ||
                        Boolean(errors.partnerPlaylistAlias)
                    }
                    isSearching={isSearching}
                    pinAssetItem={pinAssetItem}
                    removeAssetItem={removeAssetItem}
                />
            </form>
        </ScreenContainer>
    );
}
